import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import GlobalItems from 'components/Item'
import ListFilters from 'components/Item/ListFilters'
import Layout from 'components/Layout'

import { loadFilteredItems } from 'store/Items/actions'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import { loadPriceLevels } from 'store/PriceLevels/actions'
import { loadSalesEventTypes } from 'store/SalesEventTypes/actions'
import { fetchReportingCategories } from 'store/ReportingCategories/actions'
import { loadModifierGroups } from 'store/ModifierGroups/actions'
import { loadMenus } from 'store/Menus/actions'
import { loadRevenueCenters } from 'store/RevenueCenters/actions'
import { getQueryStringParameters } from 'store/AppState/selector'
import { loadItemMappings } from 'store/ItemMappings/actions'
import { getIsItemsLoading } from 'store/Items/selector'
import { getIsPriceLevelsLoading } from 'store/PriceLevels/selectors'
import { getIsRevenueCentersLoading } from 'store/RevenueCenters/selector'
import { getIsCategoriesLoading } from 'store/Categories/selectors'
import { getIsSalesEventsLoading } from 'store/SalesEvents/selectors'
import { getIsMenusLoading } from 'store/Menus/selectors'
import { getIsItemMappingsLoading } from 'store/ItemMappings/selectors'
import { loadDisplayCategories } from 'store/DisplayCategories/actions'
import { getIsLoadingDisplayCategories } from 'store/DisplayCategories/selectors'
import { setItemOptimisticListImage } from 'store/Items/actions'
import useResetHistoryStateValue from 'hooks/useResetHistoryStateValue'

const Items = () => {
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const resetHistoryStateValue = useResetHistoryStateValue(
    'updatedResourceUuid'
  )

  const currentVenueUuid = useSelector(getCurrentVenueUuid)
  const queryStringParameters = useSelector((state) =>
    getQueryStringParameters(state, pathname)
  )
  const isLoadingItems = useSelector(getIsItemsLoading)
  const isLoadingPriceLevels = useSelector(getIsPriceLevelsLoading)
  const isLoadingRevenueCenters = useSelector(
    getIsRevenueCentersLoading
  )
  const isLoadingCategories = useSelector(getIsCategoriesLoading)
  const isLoadingSalesEvents = useSelector(getIsSalesEventsLoading)
  const isMenusLoading = useSelector(getIsMenusLoading)
  const isItemMappingsLoading = useSelector(getIsItemMappingsLoading)
  const isLoadingDisplayCategories = useSelector(
    getIsLoadingDisplayCategories
  )

  const queryStringParametersWithVenue = {
    ...queryStringParameters,
    venueUuid: currentVenueUuid,
  }

  const isLoading =
    isLoadingItems ||
    isLoadingPriceLevels ||
    isLoadingRevenueCenters ||
    isLoadingCategories ||
    isLoadingSalesEvents ||
    isMenusLoading ||
    isItemMappingsLoading ||
    isLoadingDisplayCategories

  useEffect(() => {
    // reset the `updatedResourceUuid` in the location state
    // to ensure that the displayed image is downloaded from the source
    // rather than being retrieved from redux storage, where it might not exist.
    // In this case without reseting state when user uploads a new image and refreshes the page,
    // the image doesn't exist in the redux store, but updatedResourceUuid is set
    // so there is no image to display from store.
    resetHistoryStateValue('updatedResourceUuid')

    if (!isEmpty(currentVenueUuid)) {
      dispatch(loadPriceLevels(currentVenueUuid))
      dispatch(loadSalesEventTypes(currentVenueUuid))
      dispatch(loadModifierGroups(currentVenueUuid))
      dispatch(loadDisplayCategories({ venueUuid: currentVenueUuid }))
      dispatch(fetchReportingCategories(currentVenueUuid))
      dispatch(loadMenus(currentVenueUuid))
      dispatch(loadRevenueCenters(currentVenueUuid))
      dispatch(loadFilteredItems(queryStringParametersWithVenue))
      dispatch(loadItemMappings(currentVenueUuid))
    }

    return () => {
      setItemOptimisticListImage({})
    }
    //eslint-disable-next-line
  }, [currentVenueUuid, dispatch])

  return (
    <Layout
      mainContent={
        <GlobalItems
          queryStringParameters={queryStringParametersWithVenue}
          isLoading={isLoading}
        />
      }
      navbarContent={
        <ListFilters
          queryStringParameters={queryStringParametersWithVenue}
          key={queryStringParameters?.searchFor}
        />
      }
      isSidebar
      title="Items"
    />
  )
}

export default Items
